import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PartnerAccount } from './partner-account.js';
import { Subscription } from './subscription.js';

export class Organization extends DataModel {
  static readonly TYPE_ID: string = '0886902cdb2e434fafa5e3a82d6fa26d';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
        public readonly uniqueId: string,
        public readonly appStoreId: string,
        public readonly creatorId: string,
        public readonly name: string,
        public readonly creationDate: dayjs.Dayjs,
        public subscription: Subscription | null = null,
  ) {
    super();
  }

  get $typeId(): string {
    return Organization.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, Organization.SCHEMA_VERSION);
  }

  static fromAccount(account: PartnerAccount): Organization {
    return new Organization(
        account.uniqueId,
        uuid().toUpperCase(),
        account.uniqueId,
        account.formattedName,
        dayjs(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): Organization {
    return new Organization(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'appStoreId'),
        MapUtils.get<string>(map, 'creatorId'),
        MapUtils.get<string>(map, 'name'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.fromDataOrNull<Subscription>(MapUtils.getOrNull<SerializedData>(map, 'subscription')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'appStoreId': this.appStoreId,
      'creatorId': this.creatorId,
      'name': this.name,
      'creationDate': context.encodeDate(this.creationDate),
      'subscription': context.toDataOrNull(this.subscription),
    };
  }

  clone(): this {
    return new Organization(
        this.uniqueId,
        this.appStoreId,
        this.creatorId,
        this.name,
        TimeZone.cloneDate(this.creationDate),
        this.subscription?.clone() ?? null,
    ) as this;
  }

  isSame(other: Organization): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
          other.appStoreId === this.appStoreId &&
          other.creatorId === this.creatorId &&
          other.name === this.name &&
          other.creationDate === this.creationDate &&
          isSameData(other.subscription, this.subscription);
  }
}

export class OrganizationBuilder extends DataModelBuilder<Organization> {
  build(context: SerializationContext, map: SerializedData): Organization {
    return Organization.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(Organization.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Organization.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `subscription` of type {@link Subscription}
     *  - add `appStoreId` of type {@link string}
     */
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'appStoreId', uuid().toUpperCase());

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
